import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControl,
    FormLabel,
    Stack,
    SxProps,
} from '@mui/material';
import { headingFontBold, vars } from '../../../../../Theme';
import {
    ICreateReportFromTemplateErrorState,
    ICreateReportFromTemplateMainState,
} from '../CreateReportDialog';

import RadioBtnGroup from '../../_parts/RadioBtnGroup';
import { mdiChevronDown, mdiCogOutline, mdiInformationOutline } from '@mdi/js';
import AppIcon from '../../../Icons/AppIcon';
import SelectTemplate from '../../_parts/SelectTemplate';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import {
    CreateReportFromTemplateDialogEnum,
    SelectReportVersionEnum,
} from '../../../../../redux/project/documents/sliceProjectReportTemplate';
import SelectedDocumentTypes from './SelectedDocumentTypes';
import { IReportTemplate } from '../../../../../types/reportTemplate';
import InputEmail from '../../_parts/InputEmail';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const accordionSx = {
    '&:before': {
        display: 'none',
    },
    boxShadow: 'none',
};
const accordionSummarySx = {
    '& .MuiAccordionSummary-content': { m: 0, gap: '8px', alignItems: 'center' },
    '& .MuiAccordionSummary-content.Mui-expanded': {
        m: 0,
    },
    p: '0px',
    m: 0,
    minHeight: '40px',
    fontFamily: headingFontBold,
    fontSize: '16px',
};

export default ({
    mainState,
    handleChange,
    showErrorState,
    handleChangeTemplate,
}: ICreateReportContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'createReportDialog' });
    const [expanded, setExpanded] = React.useState(false);

    const versionToIncludeOptions = [
        { key: SelectReportVersionEnum.SelectedDocuments, label: t('selectedDocuments') },
        {
            key: SelectReportVersionEnum.SelectedDocumentsAllIndexes,
            label: t('selectedDocumentsIncludingFormerVersion'),
        },
    ];

    const databaseVersionsToIncludeOptions = [
        { key: SelectReportVersionEnum.DocumentsCurrentIndex, label: t('documentsNewestIndex') },
        {
            key: SelectReportVersionEnum.DocumentsAllIndexes,
            label: t('documentsAllIndex'),
        },
        {
            key: SelectReportVersionEnum.DocumentsFormula,
            label: t('documentsFormula'),
        },
    ];

    const { templates, selectedTemplate, docTypes, typeOfDialog } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsReportFromTemplate,
    );

    useEffect(() => {
        showErrorState.selectedDocumentTypes && setExpanded(true);
    }, [showErrorState]);

    const toggleAcordion = () => {
        setExpanded(prev => !prev);
    };

    const onSelectVersionToInclude = (key: string) => {
        handleChange({ field: 'selectedVersionToInclude', value: key });
    };

    return (
        <Stack spacing={'12px'}>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('chooseTemplate')}</FormLabel>
                <SelectTemplate
                    templates={templates}
                    selectedTemplate={selectedTemplate}
                    handleChangeTemplate={handleChangeTemplate}
                />
            </FormControl>

            {typeOfDialog === CreateReportFromTemplateDialogEnum.Database && (
                <FormControl required>
                    <Stack
                        direction={'row'}
                        sx={{ justifyContent: 'space-between' }}
                    >
                        <Stack direction='row' spacing={1}>
                            <FormLabel sx={formLabelSx}>{t('recipient')}</FormLabel>
                            <AppIcon path={mdiInformationOutline} tooltipText={t('recipientInfotext')} tooltipPlacement='top-start' />
                        </Stack>
                    </Stack>
                    <InputEmail
                        emails={mainState.sendTo}
                        setEmails={handleChange}
                        field='sendTo'
                        placeholder={t('enterRecipients')}
                        hasError={showErrorState.sendTo}
                        errorMessage={t('errorInput')}
                    />
                </FormControl>
            )}

            <Accordion disableGutters sx={accordionSx} expanded={expanded}>
                <AccordionSummary
                    expandIcon={<AppIcon path={mdiChevronDown} color={vars.font} size={1} />}
                    sx={accordionSummarySx}
                    onClick={toggleAcordion}
                >
                    <AppIcon path={mdiCogOutline} color={vars.font} size={1} />
                    {t('advancedExportSettings')}
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pt: '8px' }}>
                    <RadioBtnGroup
                        label={t(`selectVersionToInclude`)}
                        value={mainState.selectedVersionToInclude}
                        onSelect={onSelectVersionToInclude}
                        options={
                            typeOfDialog === CreateReportFromTemplateDialogEnum.Database
                                ? databaseVersionsToIncludeOptions
                                : versionToIncludeOptions
                        }
                        containerSx={{ m: 0, marginBottom: '16px' }}
                        border={true}
                    />
                    <SelectedDocumentTypes
                        label={t('selectDocumentTypes')}
                        docTypeOptions={docTypes}
                        mainState={mainState}
                        field='selectedDocumentTypes'
                        handleChange={handleChange}
                        showErrorState={showErrorState.selectedDocumentTypes}
                    />
                </AccordionDetails>
            </Accordion>
        </Stack>
    );
};

interface ICreateReportContentProps {
    mainState: ICreateReportFromTemplateMainState;
    handleChange: ({ field, value }: { field: string; value: string | string[] }) => void;
    showErrorState: ICreateReportFromTemplateErrorState;
    handleChangeTemplate: (template: IReportTemplate) => void;
}
